import React, { useState } from 'react';
import { ArrowLeft, FloppyDisk, Spinner } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UniversalPickerModal } from 'shared/components/UniversalPickerModal';
import { useCategoryEditorState } from './useCategoryEditorState';
import { CategoryBasicsTab } from './CategoryBasicsTab';
import { QuestionsTab } from './QuestionsTab';
import { CategoryPreviewTab } from './CategoryPreviewTab';
import { PageInfoBanner } from '@/shared/components/walkthrough';

const DueDiligenceCategoryEditorPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('basics');
  const state = useCategoryEditorState();

  const {
    formData,
    setFormData,
    questions,
    expandedQuestions,
    fileLabels,
    conditionsClipboard,
    isEditMode,
    isLoading,
    isInitialLoading,
    documentLabelPickerOpen,
    closeDocumentLabelPicker,
    handleDocumentLabelSelected,
    openDocumentLabelPicker,
    handleAddQuestion,
    handleUpdateQuestion,
    handleRemoveQuestion,
    handleMoveQuestion,
    handleDuplicateQuestion,
    toggleQuestionExpanded,
    handleAddAnswerOption,
    handleUpdateAnswerOption,
    handleRemoveAnswerOption,
    handleCopyConditions,
    handlePasteConditions,
    handleSubmit,
    navigate,
  } = state;

  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto pb-12">
      <div className="px-4 pt-4">
        <PageInfoBanner pageId="dd-category-editor" />
      </div>

      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-background border-b pb-4 pt-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/settings#dueDiligence')}
              title="Terug naar instellingen"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-semibold">
                {isEditMode ? 'Categorie bewerken' : 'Nieuwe categorie'}
              </h1>
              {formData.name && (
                <p className="text-sm text-muted-foreground">{formData.name}</p>
              )}
            </div>
          </div>
          <Button onClick={handleSubmit} disabled={isLoading} data-tour="dd-editor-save">
            {isLoading ? (
              <Spinner className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <FloppyDisk className="h-4 w-4 mr-2" weight="duotone" />
            )}
            {isLoading ? 'Opslaan...' : 'Opslaan'}
          </Button>
        </div>
      </div>

      {/* Tab layout */}
      <Tabs value={activeTab} onValueChange={setActiveTab} data-tour="dd-editor-tabs">
        <TabsList className="mb-6">
          <TabsTrigger value="basics" data-tour="dd-editor-tab-basics">Basisinformatie</TabsTrigger>
          <TabsTrigger value="questions" data-tour="dd-editor-tab-questions">
            Vragen
            {questions.length > 0 && (
              <span className="ml-1.5 text-xs bg-muted px-1.5 py-0.5 rounded-full">
                {questions.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="preview" data-tour="dd-editor-tab-preview">Overzicht</TabsTrigger>
        </TabsList>

        <TabsContent value="basics">
          <CategoryBasicsTab
            formData={formData}
            setFormData={setFormData}
            isEditMode={isEditMode}
          />
        </TabsContent>

        <TabsContent value="questions">
          <QuestionsTab
            questions={questions}
            expandedQuestions={expandedQuestions}
            fileLabels={fileLabels}
            conditionsClipboard={conditionsClipboard}
            onAddQuestion={handleAddQuestion}
            onUpdateQuestion={handleUpdateQuestion}
            onRemoveQuestion={handleRemoveQuestion}
            onMoveQuestion={handleMoveQuestion}
            onDuplicateQuestion={handleDuplicateQuestion}
            toggleQuestionExpanded={toggleQuestionExpanded}
            onAddAnswerOption={handleAddAnswerOption}
            onUpdateAnswerOption={handleUpdateAnswerOption}
            onRemoveAnswerOption={handleRemoveAnswerOption}
            onCopyConditions={handleCopyConditions}
            onPasteConditions={handlePasteConditions}
            onOpenDocumentLabelPicker={openDocumentLabelPicker}
          />
        </TabsContent>

        <TabsContent value="preview">
          <CategoryPreviewTab
            formData={formData}
            questions={questions}
            fileLabels={fileLabels}
          />
        </TabsContent>
      </Tabs>

      {/* Document label picker modal */}
      <UniversalPickerModal
        isOpen={documentLabelPickerOpen}
        mode="file-label"
        title="Selecteer document label"
        onClose={closeDocumentLabelPicker}
        onConfirm={(label) => handleDocumentLabelSelected(label as { id: number } | null)}
        config={{
          databaseFileLabels: fileLabels,
        }}
      />
    </div>
  );
};

export default DueDiligenceCategoryEditorPage;
